import { useCallback, useMemo } from "react";

import {
  InfiniteData,
  QueryKey,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { endOfToday, startOfToday, subDays } from "date-fns";

import {
  eventsApi as eventsApiBase,
  retrieveEventsApi,
} from "src/api/endpoints";
import { DateRange } from "src/base-components/DateRangePicker";
import {
  EventConfigEnriched,
  EventConfigPatch,
} from "src/clients/features-control";
import {
  EventResponse as DeserializedEventBase,
  EventFilterRequest,
  EventFilterRequestEnvironmentEnum,
  GetEventByIdEventsEventTypeIdGetEnvironmentEnum,
  GetEventsEventsGetEnvironmentEnum,
  PaginationMetadata,
  QueryStatus,
  RevealEventFieldEventsEventTypeIdRevealFieldGetEnvironmentEnum,
} from "src/clients/features-retrieve";
import {
  EVENT_FILTER_MAX_POLL_TIME_MS,
  EVENT_FILTER_MAX_RETRIES,
  EVENT_FILTER_PAGE_SIZE,
  RATE_LIMIT_MESSAGE,
  RATE_LIMIT_RETRY_DELAY_MS,
} from "src/eventsCatalogue/filters/constants";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import {
  convertFormDataToBackendFilter,
  getEventProperties,
} from "src/eventsCatalogue/filters/utils";
import { useEventFiltersUrl } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import { findEventSchema } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { DO_NOT_RETRY_ON_404 } from "src/utils/query";

export const PREFIX_CACHE_KEY_LIST = "events";
const ONE_HOUR = 1000 * 60 * 60;

export type DeserializedEvent = DeserializedEventBase & {
  payload: {
    id: string;
    timestamp: string;
    [key: string]: unknown;
    _display_symbol: string;
    _id: string;
    _type: string;
  };
};

export const eventSchemaKeys = {
  all: ["eventConfigs"] as const,
  workspaceList: (workspaceId: string) =>
    [...eventSchemaKeys.all, workspaceId] as const,
  event: (workspaceId: string, eventType: string) =>
    [...eventSchemaKeys.workspaceList(workspaceId), { eventType }] as const,
};

type EventsParams = {
  timeWindow?: { from?: string | null; to?: string | null };
  filters?: Record<string, string | null>;
};

export const eventsKeys = {
  all: (workspaceId: string) => [PREFIX_CACHE_KEY_LIST, workspaceId] as const,
  schemaEvents: (
    workspaceId: string,
    eventType?: string,
    entityId?: string | null,
  ) => [...eventsKeys.all(workspaceId), eventType, entityId] as const,
  environmentEvents: (
    workspaceId: string,
    eventType: string,
    environment: string,
    entityId?: string | null,
    params?: EventsParams,
  ) =>
    [
      ...eventsKeys.schemaEvents(workspaceId, eventType, entityId),
      environment,
      {
        timeWindowFrom: params?.timeWindow?.from,
        timeWindowTo: params?.timeWindow?.to,
        ...params?.filters,
      },
    ] as const,
  eventsByIds: (
    workspaceId: string,
    environment: Environment,
    eventType: string,
    // We could pass whole eventID object but it is harder to handle
    // Just string IDs enough for query key
    eventIds: string[],
    timeWindow: { from: string; to: string },
  ) =>
    [
      ...eventsKeys.all(workspaceId),
      "bulk",
      environment,
      eventType,
      eventIds,
      timeWindow,
    ] as const,
  filteredEvents: (
    workspaceId: string,
    eventType: string,
    entityId?: string | null,
    environment?: Environment,
    filterParams?: EventFilterRequest | null,
  ) => [
    ...eventsKeys.all(workspaceId),
    "filtered",
    eventType,
    entityId,
    environment,
    ...(filterParams
      ? [
          filterParams.start_timestamp,
          filterParams.end_timestamp,
          filterParams.filter_condition,
        ]
      : []),
  ],
};

export const useEventSchemas = (
  options?: Pick<UseQueryOptions<EventConfigEnriched[]>, "enabled">,
) => {
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const eventsApi = eventsApiBase(`${workspace.base_url}`);

  return useQuery({
    queryKey: eventSchemaKeys.workspaceList(workspace.id),
    queryFn: async () => {
      const response = await eventsApi.getAllEventConfigsEventsGet();
      return response.data;
    },
    staleTime: ONE_HOUR,
    ...options,
    enabled: featuresEventsEnabled && options?.enabled !== false,
  });
};

export const useEventSchema = (eventType: string) => {
  const { workspace } = useWorkspaceContext();
  const eventsApi = eventsApiBase(`${workspace.base_url}`);

  return useQuery({
    queryKey: eventSchemaKeys.event(workspace.id, eventType),
    queryFn: async () => {
      const response = await eventsApi.getEventConfigEventsEventTypeGet({
        eventType,
      });
      return response.data;
    },
    enabled: !!eventType,
    staleTime: ONE_HOUR,
  });
};

export const useUpdateEventType = () => {
  const { workspace } = useWorkspaceContext();
  const eventsApi = eventsApiBase(workspace.base_url!);

  return useMutation({
    mutationFn: async ({
      etag,
      eventType,
      ...EventConfigEnriched
    }: EventConfigPatch & { etag: string; eventType: string }) => {
      const response = await eventsApi.updateEventConfigEventsEventTypePatch({
        eventType,
        eventConfigPatch: EventConfigEnriched,
        ifMatch: etag,
      });
      return response.data;
    },
    onSuccess: (_, { eventType }) => {
      queryClient.invalidateQueries({
        queryKey: eventSchemaKeys.event(workspace.id, eventType),
      });
      queryClient.invalidateQueries({
        queryKey: eventSchemaKeys.workspaceList(workspace.id),
      });
      queryClient.invalidateQueries({
        queryKey: eventsKeys.schemaEvents(workspace.id, eventType),
      });
    },
  });
};

type Environment = "sandbox" | "live";

type UseEventsParams = {
  eventType: string;
  environment: Environment;
  timeWindow?: { from?: string | null; to?: string | null };
  entityId?: string | null;
  filters?: Record<string, string | null>;
};

export const useEvents = (
  params: UseEventsParams,
  options?: Pick<UseQueryOptions<DeserializedEvent[], AxiosError>, "enabled">,
) => {
  const { workspace } = useWorkspaceContext();
  const eventsRetrieveApi = retrieveEventsApi(`${workspace.base_url}`);

  return useQuery<DeserializedEvent[], AxiosError>({
    queryKey: eventsKeys.environmentEvents(
      workspace.id,
      params.eventType,
      params.environment,
      params.entityId,
      params,
    ),
    queryFn: async () => {
      const response = await eventsRetrieveApi.getEventsEventsGet(
        {
          eventType: params.eventType,
          startTimestamp:
            params.timeWindow?.from || subDays(startOfToday(), 7).toISOString(),
          endTimestamp: params.timeWindow?.to || endOfToday().toISOString(),
          environment: params.environment as GetEventsEventsGetEnvironmentEnum,
          entityId: params.entityId,
        },
        {
          params: params.filters,
        },
      );
      return response.data as DeserializedEvent[];
    },
    enabled: !!params.eventType && options?.enabled !== false,
  });
};

type UseEventParams = {
  eventType: string;
  eventId?: string | null;
  environment: Environment;
  timeWindow: { from?: string | null; to?: string | null };
};

export const useEvent = (
  params: UseEventParams,
  options?: Pick<
    UseQueryOptions<DeserializedEvent, AxiosError>,
    "enabled" | "retry"
  >,
) => {
  const { workspace } = useWorkspaceContext();
  const eventsRetrieveApi = retrieveEventsApi(`${workspace.base_url}`);

  return useQuery<DeserializedEvent, AxiosError>({
    queryKey: [
      PREFIX_CACHE_KEY_LIST,
      params.eventType,
      params.eventId,
      params.environment,
      params.timeWindow?.from,
      params.timeWindow?.to,
    ],
    queryFn: async () => {
      const response = await eventsRetrieveApi.getEventByIdEventsEventTypeIdGet(
        {
          eventType: params.eventType,
          id: params.eventId!,
          environment:
            params.environment as GetEventByIdEventsEventTypeIdGetEnvironmentEnum,
          startTimestamp:
            params.timeWindow?.from || subDays(startOfToday(), 7).toISOString(),
          endTimestamp: params.timeWindow?.to || endOfToday().toISOString(),
        },
      );
      return response.data as DeserializedEvent;
    },
    ...options,
    enabled:
      !!params.eventType &&
      !!params.eventId &&
      (options?.enabled !== undefined ? options?.enabled : true),
  });
};

export const useRevealEventField = ({
  env,
  eventId,
  eventType,
  field,
  timestamp,
  enabled,
}: {
  env: string;
  eventId: string;
  eventType: string;
  field: string;
  enabled: boolean;
  timestamp: string | null;
}) => {
  const { workspace } = useWorkspaceContext();
  const eventsRetrieveApi = retrieveEventsApi(`${workspace.base_url}`);

  return useQuery({
    queryKey: ["revealEventField", env, eventId, eventType, field],
    queryFn: () =>
      eventsRetrieveApi
        .revealEventFieldEventsEventTypeIdRevealFieldGet({
          environment:
            env as RevealEventFieldEventsEventTypeIdRevealFieldGetEnvironmentEnum,
          eventType,
          field,
          id: eventId,
          timestamp,
        })
        .then((response) => ({ value: response.data })),
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
};

// Helper function to submit query and poll for results
const submitAndPollEventFilterQuery = async (
  eventsRetrieveApi: ReturnType<typeof retrieveEventsApi>,
  params: EventFilterRequest,
  cursor: string | null | undefined,
): Promise<{
  events: DeserializedEvent[];
  pagination: PaginationMetadata | null;
}> => {
  // Submit the query with cursor and retry logic for 429 errors
  let retryCount = 0;

  const submitQuery = async (): Promise<string> => {
    try {
      const submitResponse =
        await eventsRetrieveApi.submitEventFilterQueryEventsQueryPost({
          eventFilterRequest: {
            event_type: params.event_type,
            environment: params.environment,
            start_timestamp:
              params.start_timestamp ||
              subDays(startOfToday(), 7).toISOString(),
            end_timestamp: params.end_timestamp || endOfToday().toISOString(),
            filter_condition: params.filter_condition,
            limit: EVENT_FILTER_PAGE_SIZE,
            entity_id: params.entity_id,
            cursor: cursor ?? null,
          },
        });

      const executionToken = submitResponse.data.execution_token;
      if (!executionToken) {
        throw new Error("No execution token received");
      }

      return executionToken;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        if (retryCount >= EVENT_FILTER_MAX_RETRIES) {
          throw new Error(RATE_LIMIT_MESSAGE);
        }

        retryCount++;
        const retryAfter = 5000;

        await new Promise((resolve) => setTimeout(resolve, retryAfter));
        return submitQuery();
      }
      throw error;
    }
  };

  const executionToken = await submitQuery();

  // Poll for results with timeout
  const startTime = Date.now();
  let pollRetryCount = 0;

  const pollForResults = async (): Promise<{
    events: DeserializedEvent[];
    pagination: PaginationMetadata | null;
  }> => {
    // Check if we've exceeded the maximum polling time
    if (Date.now() - startTime > EVENT_FILTER_MAX_POLL_TIME_MS) {
      throw new Error(
        "Query execution timed out. Please try again with a narrower time window or fewer filters.",
      );
    }

    try {
      const response =
        await eventsRetrieveApi.getEventFilterQueryEventsQueryExecutionTokenGet(
          {
            executionToken,
          },
        );

      const status = response.data.status;

      if (status === QueryStatus.RUNNING || status === QueryStatus.QUEUED) {
        // Wait 2 seconds and retry
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return pollForResults();
      }

      if (status === QueryStatus.FAILED || status === QueryStatus.TIMEOUT) {
        throw new Error(
          response.data.error_message ||
            "Event filter query failed. Please try again.",
        );
      }

      // Reset retry count on successful poll response
      pollRetryCount = 0;

      if (
        status === QueryStatus.SUCCEEDED &&
        response.data.events.length === 0 &&
        !cursor
      ) {
        // Return empty array if no events found and not a subsequent page
        return {
          events: [],
          pagination: null,
        };
      }

      return {
        events: response.data.events as DeserializedEvent[],
        pagination: response.data.pagination ?? null,
      };
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        if (pollRetryCount >= EVENT_FILTER_MAX_RETRIES) {
          throw new Error(RATE_LIMIT_MESSAGE);
        }

        pollRetryCount++;

        await new Promise((resolve) =>
          setTimeout(resolve, RATE_LIMIT_RETRY_DELAY_MS),
        );
        return pollForResults();
      }
      throw error;
    }
  };

  return pollForResults();
};

type EventFilterPageParam = {
  cursor: string | undefined;
  filterParams: EventFilterRequest;
};

type UseEventFiltersParams = {
  eventType: string;
  environment: EventFilterRequestEnvironmentEnum;
  startTimestamp: string;
  endTimestamp: string;
  entityId?: string;
  enabled?: boolean;
};

// Filter params passed from UI (without execution context: environment, event_type)
export type EventFilterTriggerParams = Pick<
  EventFilterRequest,
  "start_timestamp" | "end_timestamp" | "filter_condition"
>;

export const useEventsFilters = ({
  eventType,
  environment,
  startTimestamp,
  endTimestamp,
  entityId,
  enabled = true,
}: UseEventFiltersParams) => {
  const { workspace } = useWorkspaceContext();
  const eventsRetrieveApi = retrieveEventsApi(`${workspace.base_url}`);

  // Get URL-based filter state
  const {
    urlFilterParams,
    hasFilters,
    setFilters: setUrlFilters,
    clearFilters: clearUrlFilters,
  } = useEventFiltersUrl(eventType);

  // Get event schemas to convert form data to backend filter
  const { data: eventSchemas } = useEventSchemas();
  const currentEventSchema = findEventSchema(eventType, eventSchemas);
  const eventProperties = useMemo(
    () => getEventProperties(currentEventSchema),
    [currentEventSchema],
  );

  // Derive filterParams from URL state (single source of truth)
  const filterParams = useMemo<EventFilterRequest | null>(() => {
    if (!enabled) {
      return null;
    }

    // If URL has filter params, use them
    if (urlFilterParams) {
      const backendFilter = convertFormDataToBackendFilter(
        urlFilterParams.formData,
        eventProperties,
      );

      return {
        event_type: eventType,
        environment,
        entity_id: entityId,
        start_timestamp: urlFilterParams.dateRange.from.toISOString(),
        end_timestamp: urlFilterParams.dateRange.to.toISOString(),
        filter_condition: backendFilter ?? undefined,
      };
    }

    // Otherwise use default timestamps (no filter condition)
    return {
      event_type: eventType,
      environment,
      entity_id: entityId,
      start_timestamp: startTimestamp,
      end_timestamp: endTimestamp,
    };
  }, [
    enabled,
    urlFilterParams,
    eventType,
    environment,
    entityId,
    startTimestamp,
    endTimestamp,
    eventProperties,
  ]);

  const infiniteQuery = useInfiniteQuery<
    {
      events: DeserializedEvent[];
      pagination: PaginationMetadata | null;
    },
    AxiosError,
    InfiniteData<{
      events: DeserializedEvent[];
      pagination: PaginationMetadata | null;
    }>,
    QueryKey,
    EventFilterPageParam
  >({
    queryKey: eventsKeys.filteredEvents(
      workspace.id,
      eventType,
      entityId,
      environment,
      filterParams,
    ),
    queryFn: async ({ pageParam }) => {
      if (!pageParam.filterParams) {
        throw new Error("Filter params are required");
      }
      return submitAndPollEventFilterQuery(
        eventsRetrieveApi,
        pageParam.filterParams,
        pageParam.cursor,
      );
    },
    enabled: !!filterParams,
    initialPageParam: {
      cursor: undefined,
      filterParams: filterParams!,
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.pagination?.has_more || !lastPage.pagination?.next_cursor) {
        return undefined;
      }
      return {
        cursor: lastPage.pagination.next_cursor,
        filterParams: lastPageParam.filterParams,
      };
    },
    retry: false,
  });

  // Set filters - updates URL and triggers tracking
  const applyFilters = useCallback(
    (
      formData: EventFilterFormData | undefined,
      dateRange: DateRange | undefined,
      clearParamKeys?: string[],
    ) => {
      if (!dateRange?.from || !dateRange?.to) {
        return;
      }

      // Update URL (formData is optional - if undefined, only dates are updated)
      setUrlFilters(formData, dateRange, clearParamKeys);

      // Only track if formData is provided
      if (formData) {
        const backendFilter = convertFormDataToBackendFilter(
          formData,
          eventProperties,
        );

        tracker.emit(
          trackingEvents.eventFilters({
            event_type: eventType,
            environment,
            start_timestamp: dateRange.from.toISOString(),
            end_timestamp: dateRange.to.toISOString(),
            filter_condition: backendFilter ?? undefined,
            entity_id: entityId,
            organization_id: workspace.organization_id,
          }),
        );
      }
    },
    [
      setUrlFilters,
      eventProperties,
      eventType,
      environment,
      entityId,
      workspace.organization_id,
    ],
  );

  // Clear filters - clears URL and removes cached query
  const clearFilters = useCallback(() => {
    // Remove cached filtered events query
    queryClient.removeQueries({
      queryKey: eventsKeys.filteredEvents(
        workspace.id,
        eventType,
        entityId,
        environment,
        filterParams,
      ),
    });

    // Clear URL params
    clearUrlFilters();
  }, [
    workspace.id,
    eventType,
    entityId,
    environment,
    filterParams,
    clearUrlFilters,
  ]);

  const events = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.events) ?? [],
    [infiniteQuery.data?.pages],
  );

  const isPending = infiniteQuery.isLoading || infiniteQuery.isPending;

  const error = infiniteQuery.error
    ? {
        data: isAxiosError(infiniteQuery.error)
          ? infiniteQuery.error.response?.data
          : infiniteQuery.error,
        status: isAxiosError(infiniteQuery.error)
          ? infiniteQuery.error.response?.status
          : undefined,
      }
    : undefined;

  const hasFilterCondition = hasFilters;

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isRefetching,
    refetch,
  } = infiniteQuery;

  const handleFetchNextPage = useCallback(() => {
    if (filterParams && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [filterParams, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    applyFilters,
    clearFilters,
    urlFilterParams,
    events,
    hasFilterCondition,
    isPending,
    error,
    hasNextPage,
    handleFetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  };
};

type EventId = {
  id: string;
  timestamp: string;
};

type UsePinnedEventsParams = {
  eventIds: EventId[];
  environment: Environment;
  timeWindow: { from: string; to: string };
  eventType: string;
  enabled?: boolean;
};

export const useEventsByIds = (
  { eventIds, environment, timeWindow, eventType }: UsePinnedEventsParams,
  options?: Pick<UseQueryOptions<DeserializedEvent, AxiosError>, "enabled">,
) => {
  const { workspace } = useWorkspaceContext();
  const eventsRetrieveApi = useMemo(
    () => retrieveEventsApi(`${workspace.base_url}`),
    [workspace.base_url],
  );

  return useQuery({
    queryKey: eventsKeys.eventsByIds(
      workspace.id,
      environment,
      eventType,
      eventIds.map((e) => e.id),
      timeWindow,
    ),
    queryFn: async () => {
      const response = await eventsRetrieveApi.getEventsEventsGet(
        {
          startTimestamp: timeWindow.from,
          endTimestamp: timeWindow.to,
          eventType,
          environment: environment as GetEventsEventsGetEnvironmentEnum,
        },
        {
          params: {
            id_ts: eventIds.map((e) => `${e.id}:${e.timestamp}`).join(","),
          },
        },
      );

      return response.data as DeserializedEvent[];
    },
    enabled:
      options?.enabled !== false && eventIds.length > 0 && !!workspace.base_url,
    retry: DO_NOT_RETRY_ON_404,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
